import { Controller } from '@hotwired/stimulus';

import dataLayerEvents from '../../components/dataLayerEvents/dataLayerEvents';
import useAuthenticatedUser from '../../stimulus-composables/authenticated-user';

/**
 * Manage current user notifications.
 */
export default class extends Controller {
    static targets = ['list', 'unseenBadge'];

    static outlets = ['header'];

    #loading = false;

    #notificationsLoaded = false;

    connect() {
        useAuthenticatedUser(this, this.#updateView);
    }

    fetchNotifications() {
        if (this.#loading || this.#notificationsLoaded) {
            return;
        }

        this.#loading = true;
        this.element.ariaBusy = 'true';

        fetch('/compte/notifications.html', {
            method: 'GET',
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
            },
            credentials: 'include',
        }).then(async (response) => {
            if (!response.ok) {
                return;
            }

            this.listTarget.innerHTML = await response.text();
            dataLayerEvents.refreshDOM(this.listTarget);
        }).finally(() => {
            this.#notificationsLoaded = true;

            if (this.hasUnseenBadgeTarget && this.hasHeaderOutlet) {
                this.headerOutlet.showNotificationBadge();
            }

            this.#loading = false;
            this.element.ariaBusy = 'false';
        });
    }

    markNotificationsAsSeen() {
        this.#sendNotificationViewEvent();

        // Nothing to update, initial fetch is not ready
        if (!this.#notificationsLoaded) {
            return;
        }

        // Nothing to update, all notifications have been seen
        if (!this.hasUnseenBadgeTarget) {
            if (this.hasHeaderOutlet) {
                this.headerOutlet.hideNotificationBadge();
            }

            return;
        }

        const userNotificationIds = this.unseenBadgeTargets.map((element) => parseInt(element.getAttribute('data-user-notification-id'), 10));

        fetch('/compte/notifications.html', {
            method: 'PUT',
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
            },
            credentials: 'include',
            body: JSON.stringify({ userNotificationIds }),
        }).then(async (response) => {
            if (response.ok && this.hasHeaderOutlet) {
                this.headerOutlet.hideNotificationBadge();
            }
        });
    }

    removeUnseenNotificationBadge() {
        this.unseenBadgeTargets.forEach((element) => {
            element.remove();
        });
    }

    #updateView = (user) => {
        if (!user?.content?.connected) {
            return;
        }

        if (this.hasListTarget) {
            this.fetchNotifications();
        }
    };

    #sendNotificationViewEvent() {
        const counter = this.listTarget.querySelectorAll('.notification-item').length;
        const links = this.listTarget.querySelector('a') !== null;

        window.dataLayer.push({
            event: 'refonte_2025',
            eventName: 'Notification',
            eventType: 'Navigation',
            eventCat: 'Lecture notifications',
            eventAct: counter,
            eventLab: links ? 'Oui' : 'Non',
            eventContext: '',
        });
    }
}
